/* eslint-disable react/no-this-in-sfc */
import { Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { Button, Container, Row, Col } from 'react-bootstrap';
import ActionMemoSearchForm, { ActionMemoSearchFormType } from './ActionMemoSearchForm';
import { SearchObjectType } from '../../common/datatables/DataGridControlled';
import { AM_NUMBER_REGEX } from '../../../utils/Constants';
import './ActionMemoFilter.css';

interface ActionMemoFilterProps {
    setSearchObject: Function;
    searchString: string;
    searching: boolean;
    onSearch?: () => void;
}

const ActionMemoFilter = (props: ActionMemoFilterProps) => {
    const searchObjectInitial = JSON.parse(props.searchString);

    const [isfilterExpanded, setIsFilterTExpanded] = useState(searchObjectInitial.advanceSearch);
    const [refresh, setRefresh] = useState(0);

    const initialValues: ActionMemoSearchFormType = {
        amNumberSearchValue: searchObjectInitial.amNumberSearchValue || '',
        amNumber: searchObjectInitial.amNumber || '',
        supNumber: searchObjectInitial.supNumber || '',
        amNumberFrom: searchObjectInitial.amNumberFrom || '',
        amNumberTo: searchObjectInitial.amNumberTo || '',
        budgetAmountFrom:
            searchObjectInitial.budgetAmountFrom === null
                ? ''
                : searchObjectInitial.budgetAmountFrom,
        budgetAmountTo:
            searchObjectInitial.budgetAmountTo === null ? '' : searchObjectInitial.budgetAmountTo,
        textSearchValues: searchObjectInitial.textSearchValues
            ? searchObjectInitial.textSearchValues.join(' ')
            : [],
        amStatuses: searchObjectInitial.amStatuses ? searchObjectInitial.amStatuses : [],
        amCreator: searchObjectInitial.amCreator || '',
        amCreatorName: searchObjectInitial.amCreatorName || '',
        projectManagerId: searchObjectInitial.projectManagerId || '',
        projectManagerName: searchObjectInitial.projectManagerName || '',
        organizationIds: searchObjectInitial.organizationIds
            ? searchObjectInitial.organizationIds
            : [],
        organizationNames: searchObjectInitial.organizationNames
            ? searchObjectInitial.organizationNames
            : [],
        dateFrom: searchObjectInitial.dateFrom,
        dateTo: searchObjectInitial.dateTo,
        templateCodes: searchObjectInitial.templateCodes ? searchObjectInitial.templateCodes : [],
        currentApproverId: searchObjectInitial.currentApproverId || '',
        currentApproverName: searchObjectInitial.currentApproverName || '',
        contractSignerId: searchObjectInitial.contractSignerId || '',
        contractSignerName: searchObjectInitial.contractSignerName || '',
    };

    const [responseError, setResponseError] = useState('');

    const actionMemoSearchValidationSchema = Yup.object().shape({
        amNumberSearchValue: Yup.string().test(
            'am numer validation',
            'AM Number must be in XXXXXX or XXXXXX-X format',
            (amNumber) => {
                if (!amNumber) return true; // null is fine

                const regex = new RegExp(AM_NUMBER_REGEX);
                return regex.test(amNumber.toString());
            },
        ),
        amNumberFrom: Yup.number()
            .typeError('AM Number is invalid')
            .test(
                'am-number-min-max-check',
                'Minimum AM Number must be less or equal to than Maximum AM Number',
                function test(amNumberFrom) {
                    if (!amNumberFrom || !this.parent.amNumberTo) {
                        return true;
                    }
                    return amNumberFrom <= this.parent.amNumberTo;
                },
            ),
        amNumberTo: Yup.number().typeError('AM Number is invalid'),
        budgetAmountFrom: Yup.number()
            .typeError('Minimum Budget Amount is invalid')
            .test(
                'budget-min-max-check',
                'Minimum budget must be less than or equal to Maximum budget',
                function test(budgetAmountFrom) {
                    if (!budgetAmountFrom || !this.parent.budgetAmountTo) {
                        return true;
                    }
                    return budgetAmountFrom <= this.parent.budgetAmountTo;
                },
            ),
        budgetAmountTo: Yup.number().typeError('Maximum Budget Amount is invalid'),
        dateFrom: Yup.date()
            .nullable()
            .typeError('From Date is invalid')
            .test(
                'date-from-to-check',
                'Date From must be before or equal to Date To',
                function test(dateFrom) {
                    if (!dateFrom || !this.parent.dateTo) {
                        return true;
                    }
                    const fromMoment = moment(dateFrom);
                    const toMoment = moment(this.parent.dateTo);
                    return fromMoment.isSameOrBefore(toMoment);
                },
            ),
        dateTo: Yup.date().nullable().typeError('To Date is invalid'),
    });

    return (
        <Container fluid className="border-top pt-3">
            <Row>
                <Col className="mobile-hide align-self-center align-self-middle mb-3 filter-button-column">
                    <Button
                        variant="link"
                        className="btn-link text-danger"
                        onClick={() => {
                            setIsFilterTExpanded(!isfilterExpanded);
                        }}
                    >
                        <i className="fas fa-filter" />
                    </Button>
                </Col>
                <Col>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={actionMemoSearchValidationSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            let searchObject: SearchObjectType = {
                                textSearchValues:
                                    values.textSearchValues.length > 0
                                        ? values.textSearchValues.split(' ')
                                        : null,
                                textSearchType: values.textSearchValues.length > 0 ? 'AND' : null,
                                amStatuses: values.amStatuses.length > 0 ? values.amStatuses : null,
                                amNumberSearchValue:
                                    values.amNumberSearchValue === ''
                                        ? null
                                        : values.amNumberSearchValue,
                                amNumber: values.amNumber === '' ? null : values.amNumber,
                                supNumber: values.supNumber === '' ? null : values.supNumber,
                                amNumberFrom:
                                    values.amNumberFrom === '' ? null : values.amNumberFrom,
                                amNumberTo: values.amNumberTo === '' ? null : values.amNumberTo,
                                refresh,
                                advanceSearch: false,
                            };

                            if (isfilterExpanded) {
                                searchObject = {
                                    ...searchObject,
                                    advanceSearch: true,
                                    dateFrom: values.dateFrom
                                        ? new Date(values.dateFrom).toUTCString()
                                        : null,
                                    dateTo: values.dateTo
                                        ? new Date(values.dateTo).toUTCString()
                                        : null,
                                    budgetAmountFrom:
                                        values.budgetAmountFrom === ''
                                            ? null
                                            : values.budgetAmountFrom,
                                    budgetAmountTo:
                                        values.budgetAmountTo === '' ? null : values.budgetAmountTo,
                                    amCreator: values.amCreator === '' ? null : values.amCreator,
                                    amCreatorName:
                                        values.amCreatorName === '' ? null : values.amCreatorName,
                                    projectManagerId:
                                        values.projectManagerId === ''
                                            ? null
                                            : values.projectManagerId,
                                    projectManagerName:
                                        values.projectManagerName === ''
                                            ? null
                                            : values.projectManagerName,
                                    templateCodes:
                                        values.templateCodes.length > 0
                                            ? values.templateCodes
                                            : null,
                                    organizationIds:
                                        values.organizationIds.length > 0
                                            ? values.organizationIds
                                            : null,
                                    organizationNames:
                                        values.organizationNames.length > 0
                                            ? values.organizationNames
                                            : null,
                                    currentApproverId:
                                        values.currentApproverId === ''
                                            ? null
                                            : values.currentApproverId,
                                    currentApproverName:
                                        values.currentApproverName === ''
                                            ? null
                                            : values.currentApproverName,
                                    contractSignerId:
                                        values.contractSignerId === ''
                                            ? null
                                            : values.contractSignerId,
                                    contractSignerName:
                                        values.contractSignerName === ''
                                            ? null
                                            : values.contractSignerName,
                                };
                            }

                            props.setSearchObject(searchObject);
                            setRefresh(refresh + 1);
                            setSubmitting(false);

                            if (props.onSearch) {
                                props.onSearch();
                            }
                        }}
                        render={(formikProps) => (
                            <ActionMemoSearchForm
                                {...formikProps}
                                searching={props.searching}
                                responseError={responseError}
                                setResponseError={setResponseError}
                                isfilterExpanded={isfilterExpanded}
                            />
                        )}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default ActionMemoFilter;
